import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TopNavigationBar from './TopNavigationBar';
import {
	Divider,
	Grid2,
	IconButton,
	SvgIcon,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { CloseIcon } from '../assets/icons/Icons';
import { APP_VERSION } from '../appVersion';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';

interface NavigationItem {
	name: string;
	route: string;
	disabled?: boolean;
}

interface CustomDrawerProps {
	tabName: string;
	listItems: NavigationItem[];
	onClickTab: (value: string) => void;
	onClose?: () => void;
	isScreenInMobileMode?: boolean;
}

function CustomDrawer({
	tabName,
	listItems,
	onClickTab,
	onClose,
	isScreenInMobileMode = false,
}: CustomDrawerProps) {
	const drawerWidth = 216;

	function getNavigationIcon(route: string) {
		if (route === '/studio') {
			return (
				<SvgIcon fontSize="small" viewBox="0 0 24 24">
					<path d="m12 2 1.45 4.55L18 8l-4.55 1.45L12 14l-1.45-4.55L6 8l4.55-1.45L12 2Zm7 10 .9 2.1L22 15l-2.1.9L19 18l-.9-2.1L16 15l2.1-.9L19 12ZM6 14l1.2 2.8L10 18l-2.8 1.2L6 22l-1.2-2.8L2 18l2.8-1.2L6 14Z" />
				</SvgIcon>
			);
		}

		return (
			<SvgIcon fontSize="small" viewBox="0 0 24 24">
				<path d="M4 4h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H8l-5 3V6a2 2 0 0 1 2-2Zm3 5v2h10V9H7Zm0 4v2h7v-2H7Z" />
			</SvgIcon>
		);
	}

	function getIconColor(item: NavigationItem) {
		if (item.disabled) return '#2d3633';
		if (tabName === item.route || tabName.substring(0, 8) === item.route) {
			return MAIN_THEME_COLOR;
		}
		return '#616161';
	}

	return (
		<Drawer
			open
			onClose={onClose}
			sx={{
				width: isScreenInMobileMode ? '100%' : drawerWidth,
				flexShrink: 0,
				'& .MuiDrawer-paper': {
					width: isScreenInMobileMode ? 300 : drawerWidth,
					boxSizing: 'border-box',
					borderRight: '1px solid #E9EDEF',
					marginTop: isScreenInMobileMode ? '0px' : '60px',
					height: isScreenInMobileMode ? '100%' : 'calc(100% - 60px)',
					backgroundColor: '#FFFFFF',
					display: 'flex',
					flexDirection: 'column',
				},
			}}
			variant={isScreenInMobileMode ? 'temporary' : 'permanent'}
			anchor="left"
			elevation={0}>
			{isScreenInMobileMode && (
				<Toolbar
					style={{
						paddingLeft: '20px',
						minHeight: '60px',
						justifyContent: 'space-between',
					}}>
					<Typography
						fontSize={'15px'}
						fontWeight={'700'}
						color="#111B21"
						paddingLeft="5px">
						SenzMate Bot
					</Typography>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Toolbar>
			)}
			{isScreenInMobileMode && <Divider />}
			<Typography
				sx={{
					px: 2.5,
					pt: 2.5,
					pb: 1,
					fontSize: '11px',
					fontWeight: 700,
					color: '#8696A0',
					textTransform: 'uppercase',
					letterSpacing: 0,
				}}>
				Workspace
			</Typography>
			<List sx={{ px: 1.25, py: 0.5 }}>
				{listItems.map(item => {
					const selected = tabName === item.route;
					return (
						<ListItem
							key={item.route}
							disablePadding
							sx={{
								backgroundColor: selected ? '#E7F8F2' : 'white',
								width: '100%',
								borderRadius: '5px',
								mb: 0.5,
							}}>
							<ListItemButton
								component={item.disabled ? 'div' : Link}
								to={item.disabled ? undefined : item.route}
								disabled={item.disabled}
								sx={{
									minHeight: 44,
									borderRadius: '5px',
									px: 1.5,
									'&:hover': { backgroundColor: '#F0F2F5' },
								}}
								onClick={() => {
									if (!item.disabled) onClickTab(item.route);
								}}>
								<ListItemIcon
									sx={{
										minWidth: 36,
										color: getIconColor(item),
									}}>
									{getNavigationIcon(item.route)}
								</ListItemIcon>
								<Typography
									sx={{
										color: getIconColor(item),
										fontSize: '13px',
										fontWeight: 600,
										lineHeight: 1.3,
									}}>
									{item.name}
								</Typography>
							</ListItemButton>
						</ListItem>
					);
				})}
			</List>
			<Box
				sx={{
					mt: 'auto',
					px: 2.5,
					py: 2,
					borderTop: '1px solid #E9EDEF',
				}}>
				<Typography fontSize="11px" fontWeight={700} color="#667781">
					{APP_VERSION}
				</Typography>
				<Typography fontSize="10px" color="#8696A0" mt={0.35}>
					<Box
						component="span"
						sx={{ display: { xs: 'inline', sm: 'none' } }}>
						© SenzMate
					</Box>
					<Box
						component="span"
						sx={{ display: { xs: 'none', sm: 'inline' } }}>
						© SenzMate. All rights reserved.
					</Box>
				</Typography>
			</Box>
		</Drawer>
	);
}

export default function SideNavigationBar() {
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [tabName, setTabName] = useState('/');
	const [openDrawerInMobile, setOpenDrawerInMobile] = useState(false);
	const listItems: NavigationItem[] = [
		{ name: 'Conversations', route: '/chats' },
		{ name: 'Studio', route: '/studio' },
	];

	useEffect(() => {
		setTabName(location.pathname);
	}, [location.pathname]);

	function openDrawerInMobileMode() {
		setOpenDrawerInMobile(!openDrawerInMobile);
	}

	function onClickLogout() {
		localStorage.clear();
		navigate('/');
	}

	if (isMobile) {
		return (
			<>
				<CssBaseline />
				<TopNavigationBar
					onClickLogo={openDrawerInMobileMode}
					onClickLogout={onClickLogout}
				/>
				{openDrawerInMobile && (
					<CustomDrawer
						tabName={tabName}
						listItems={listItems}
						onClickTab={setTabName}
						isScreenInMobileMode={isMobile}
						onClose={openDrawerInMobileMode}
					/>
				)}
			</>
		);
	}

	return (
		<Box sx={{ display: 'flex' }}>
			<CssBaseline />
			<TopNavigationBar onClickLogout={onClickLogout} />
			<CustomDrawer
				tabName={tabName}
				listItems={listItems}
				onClickTab={setTabName}
			/>
		</Box>
	);
}
